import React, { useEffect, useState } from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import SurveyStepFooter from 'Views/Onboarding/Pages/Survey/SurveyStep/SurveyStepFooter';
import SurveyStepHeader from 'Views/Onboarding/Components/SurveyStepHeader';
import {
  FDBox,
  FDStack,
  FDTitleText,
  FDAccordionGroup,
  FDSimpleAccordion,
} from '@facet-wealth/design';
import { useSurveyContext } from 'Views/Onboarding/Pages/Survey/SurveyContext';
import { MetaCollectionRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaCollectionRenderer';
import FileUploadDropAreaWithAttachmentList
  from 'Components/ComponentLibrary/Molecules/FileUploadDropAreaWithAttachmentList';
import fr from 'facet-rest';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import { AttachmentsList } from 'Organisms/AttachmentsList/AttachmentsList';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useSurveyStepContext } from 'Views/Onboarding/Pages/Survey/SurveyStep/SurveyStepContext';
import { dataAttrs } from 'Helpers/Testing';
import UploadGroup from './UploadGroup';

const accountMetaOverrideSet = buildFacetDesignOverride()
  .nameOverride('name', {
    _component: () => FDTitleText,
    _customProps: { size: 'lg' },
  }, overrideReadOnly)
  .done();

const DocumentUploadLayout = () => {
  const { refreshOnboardingStepCollection } = useSurveyContext();
  const { displayErrorMessage } = useSnackbarContext();
  const {
    onClickNext,
  } = useSurveyStepContext();

  const {
    resource,
    onSubmit: onSubmitMetaForm,
    errors,
  } = useMetaLayoutContext();
  const [generalUploads, setGeneralUploads] = useState([]);

  const handleGeneralUploadSuccess = () => {
    refreshOnboardingStepCollection();
  };

  const handleSubmit = () => {
    onClickNext(onSubmitMetaForm);
  };

  // Fetch general uploads when the resource changes
  useEffect(() => {
    if (resource) {
      const fetchGeneralUploads = async () => {
        const uploadsCollection = await resource.withRelationship(
          fr.relationships.statements.name,
          async (uploadsRel) => uploadsRel.link('related').get(),
          () => undefined,
        );

        if (uploadsCollection) {
          setGeneralUploads(uploadsCollection.resources().list());
        }
      };

      fetchGeneralUploads().catch(err => {
        displayErrorMessage(err, 'Failed to fetch uploaded files');
      });
    }
  }, [resource?.instanceId()]);

  if (!resource) {
    return null;
  }

  return (
    <FDBox>
      <SurveyStepHeader
        title={resource.attr('title')}
        subtitle={resource.attr('subtitle')}
      />
      <FDStack width={'100%'} direction={'column'} paddingY={2} spacing={3} alignItems={'center'}>
        <FDStack width={'100%'} maxWidth={600} direction={'column'} spacing={2} alignItems={'center'}>
          {resource.withRelationship(fr.relationships.documentUpload.name,
            (rel) => {
              return (
                <FileUploadDropAreaWithAttachmentList
                  trailingTitleText={'documents'}
                  subtitle={'JPG, PNG, PDF with max file size 50MB'}
                  url={rel.link('related').url()}
                  acceptedFileTypes="image/*,.pdf"
                  multiple
                  onSuccess={() => handleGeneralUploadSuccess(true)}
                  onError={() => handleGeneralUploadSuccess(false)}
                  errorMessage={errors.length > 0 ? 'Please upload at least one document' : undefined}
                  onDelete={async () => refreshOnboardingStepCollection()}
                  confirmOnDelete
                />
              );
            },
            () => null)}
        </FDStack>
      </FDStack>
      <FDStack direction="column" spacing={2} width="100%">
        <FDAccordionGroup>
          <FDSimpleAccordion
            expansionID="attachments-accordion"
            title={resource.attr('progressDetail')}
            gutters={false}
            {...dataAttrs('attachments-accordion', resource)}
          >
            <AttachmentsList
              attachments={generalUploads}
              onDelete={async () => refreshOnboardingStepCollection()}
              confirmOnDelete
            />
          </FDSimpleAccordion>
        </FDAccordionGroup>
        <MetaCollectionRenderer
          collection={resource.attr('uploadGroups')}
          layout={UploadGroup}
          metaOverrideSet={accountMetaOverrideSet}
          {...dataAttrs('upload-groups', resource)}
        />
      </FDStack>
      <SurveyStepFooter onSubmit={handleSubmit} />
    </FDBox>
  );
};

DocumentUploadLayout.propTypes = {};

DocumentUploadLayout.defaultProps = {};

export default DocumentUploadLayout;
